// Única fuente de verdad de "¿este carrito admite un cupón?" en el web.
// Antes esta lógica vivía inline en checkPromo() de CartPage.jsx, con un
// criterio propio que no coincidía con el resto de la plataforma.
//
// El BACKEND es la autoridad del cobro: al crear la orden descarta el cupón
// (`appliedDiscountEfectivo = null`) cuando este criterio se cumple, en sus
// dos endpoints —`algunProductoConOferta` inline en createOrder.post.ts
// (Webpay/OneClick) y `algunProductoConOfertaMP` en simaMercadoPago.ts
// (Mercado Pago)—. Un cliente MÁS PERMISIVO que el backend mostraría un total
// con cupón que el backend no aplica: descuadre entre lo mostrado y lo
// cobrado. Uno más restrictivo priva al usuario de un cupón válido.
//
// Los tres criterios, idénticos a los del backend y a hasCartOffers() de
// Sima-appmovil (src/utils/pricing.js):
//   - `descuento_porcentaje > 0`
//   - `descuento_monto_fijo > 0`
//   - `descuento_dosxuno > 0` con cantidad >= 2
//
// Quedan FUERA a propósito, y conviene no reintroducirlos:
//
// - `producto_oferta` (oferta directa): sí puede convivir con un cupón, y así
//   lo tratan los dos endpoints del backend. Eso NO la vuelve un campo de
//   presentación: `resolverPrecioProducto` (precioProducto.js) y
//   `calculateTotals` (store.js) la usan como precio efectivo.
//
// - `descuento_precio_final` (descuento de máquina): este módulo dejó de
//   mirarlo al unificar el criterio con la app y el backend, que nunca lo
//   consideraron. El comentario anterior de checkPromo() lo justificaba
//   citando la regla #3 de specs/cart-pricing-and-discounts.md, pero esa
//   regla define la PRECEDENCIA DEL PRECIO, no qué ofertas excluyen un cupón
//   — la sección "Cupón de descuento" de esa spec no fija ninguna exclusión.
//   Además el campo viene casi siempre positivo aunque no haya oferta (el
//   backend devuelve ahí el precio BASE en getProductsNearby.get.ts), así que
//   usarlo como señal de oferta bloqueaba cupones de más. Ese bug ya ocurrió
//   en el backend de Mercado Pago y está documentado en simaMercadoPago.ts.

/**
 * ¿Alguna línea del carrito trae una oferta que excluya el cupón de carrito?
 *
 * @param {Array} cart Líneas ya filtradas por máquina. La cantidad se lee de
 *   `producto_cant` (nombre que usan el web y el backend) o de `cantidad`
 *   (el que usa la app), para que el módulo sirva con cualquiera de los dos
 *   shapes sin que un rename silencioso desactive el criterio del 2x1.
 * @returns {boolean} true si el cupón NO debe poder aplicarse.
 */
export const hayOfertaQueExcluyeCupon = (cart) =>
  (cart || []).some((item) => {
    const cantidad = Number(item?.producto_cant ?? item?.cantidad) || 0;
    const dosxuno = Number(item?.descuento_dosxuno) || 0;
    const descuentoPorcentaje = Number(item?.descuento_porcentaje) || 0;
    const descuentoMontoFijo = Number(item?.descuento_monto_fijo) || 0;

    if (descuentoPorcentaje > 0) return true;
    if (descuentoMontoFijo > 0) return true;
    if (dosxuno > 0 && cantidad >= 2) return true;
    return false;
  });
